// src/utils/googleAuth.js
// Google sign-in resilience helpers, shared by LoginScreen and SignUpWizard.
//
// signInWithPopup silently hangs on mobile web when the popup can't message
// the opener back (in-app browsers, some mobile Chrome/Safari states) — the
// Google account gets created server-side but the page spins forever. A real
// recruit hit exactly this on 2026-07-22. On mobile web we use the full-page
// redirect flow instead, which is reliable because authDomain (critcash.com)
// is the same origin as the app.

import { Platform } from 'react-native';
import { isDesktopApp } from './platform';

// Mobile-web detection. In-app browsers (Discord, Instagram, etc.) all carry
// a mobile UA, so they take the redirect path too.
export function isMobileWeb() {
  return (
    Platform.OS === 'web' &&
    typeof navigator !== 'undefined' &&
    /Android|iPhone|iPad|iPod|IEMobile|Opera Mini|Mobile|CriOS|FxiOS/i.test(navigator.userAgent || '')
  );
}

// Google sign-in takes the full-page redirect on mobile web AND inside the
// CritCast desktop app (DESKTOP_APP_DESIGN_2026-10-20.md S2.6): the app's
// left view goes /__/auth/handler -> accounts.google.com -> back, which
// needs no popup or opener plumbing inside Electron.
export function preferRedirectForGoogle() {
  return isMobileWeb() || isDesktopApp();
}

// Firestore reads have no built-in timeout — a blocked connection retries
// forever and the caller's spinner never resolves. Cap it so the worst case
// is a "try again" error instead of infinite buffering.
export function withTimeout(promise, ms = 10000, label = 'The request') {
  let timer;
  return Promise.race([
    promise,
    new Promise((_, reject) => {
      timer = setTimeout(
        () => {
          const e = new Error(`${label} timed out. Please check your connection and try again.`);
          e.code = GOOGLE_TIMEOUT_CODE;
          reject(e);
        },
        ms
      );
    }),
  ]).finally(() => clearTimeout(timer));
}

// The code withTimeout's rejection carries, so googleSignInErrorText can tell
// it from an SDK error without reading the message.
export const GOOGLE_TIMEOUT_CODE = 'critcast/timeout';

export const GOOGLE_SIGN_IN_LINES = Object.freeze({
  failed: 'Google sign-in did not finish. Please try again.',
  timeout: 'Sign-in timed out. Please check your connection and try again.',
  popupBlocked: 'Your browser blocked the Google sign-in window. Allow pop-ups for this site and try again.',
  network: 'Check your connection and try again.',
  tooMany: 'Too many attempts. Please wait a bit and try again.',
  otherSignIn: 'This email already has a CritCast account. Sign in the way you first signed up.',
  disabled: 'This account is disabled. Contact support@critcash.com for help.',
});

// Codes the member caused by closing or replacing the Google window: no line.
const GOOGLE_SILENT_CODES = [
  'auth/popup-closed-by-user',
  'auth/cancelled-popup-request',
  'auth/redirect-cancelled-by-user',
  'auth/user-cancelled',
];

// e = a Firebase AuthError (or withTimeout's rejection) from a Google sign-in
// leg, popup or redirect. Returns the member line, or null when nothing should
// show. Never returns e.message: an AuthError's message is SDK text, and its
// customData can carry OAuth tokens, so callers log e.code only (S2.4).
export function googleSignInErrorText(e) {
  const code = e && typeof e.code === 'string' ? e.code : '';
  if (GOOGLE_SILENT_CODES.includes(code)) return null;
  switch (code) {
    case GOOGLE_TIMEOUT_CODE:
      return GOOGLE_SIGN_IN_LINES.timeout;
    case 'auth/popup-blocked':
      return GOOGLE_SIGN_IN_LINES.popupBlocked;
    case 'auth/network-request-failed':
      return GOOGLE_SIGN_IN_LINES.network;
    case 'auth/too-many-requests':
      return GOOGLE_SIGN_IN_LINES.tooMany;
    case 'auth/account-exists-with-different-credential':
    case 'auth/credential-already-in-use':
    case 'auth/email-already-in-use':
      return GOOGLE_SIGN_IN_LINES.otherSignIn;
    case 'auth/user-disabled':
      return GOOGLE_SIGN_IN_LINES.disabled;
    default:
      return GOOGLE_SIGN_IN_LINES.failed;
  }
}
